// The registry of Dugzi products (D-028): who's live, where each lives, how to open one in the same account.
import type { ComponentType } from 'react';
import { productHost, productName } from '../brand/brand';
import { productAvailable } from '../release-scope';
import { isId } from '../ids';

/** `live` apps are listed; `planned` ones stay hidden until they ship (flip one word to add them). */
export type DugziAppStatus = 'live' | 'planned';

/** One Dugzi product in the app launcher. Adding a product to Dugzi means adding one entry to `DUGZI_APPS`. */
export interface DugziApp {
  id: string;
  name: string;
  /** Its subdomain next to Dugzi Account: accounts.<domain> becomes <host>.<domain> (the same rule as every app's config). */
  host: string;
  /** Where the app starts. */
  home: string;
  /** Its icon; Dugzi's own products get theirs in the launcher (so the icons load with the launcher's panel). */
  Icon?: ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;
  status: DugziAppStatus;
  /** "New …" shortcut: a path in that app that creates one and opens it. */
  create?: { label: string; path: string };
}

/** One entry: its name and subdomain come from the brand config (ui/src/brand/brand.json), the rest is product behaviour. */
const app = (id: string, rest: Omit<DugziApp, 'id' | 'name' | 'host'>): DugziApp => ({ id, name: productName(id), host: productHost(id), ...rest, status: productAvailable(id) ? rest.status : 'planned' });

/** The registry: every Dugzi product, in launcher order. */
export const DUGZI_APPS: readonly DugziApp[] = [
  app('mail', { home: '/inbox', status: 'live' }),
  app('calendar', { home: '/', status: 'live' }),
  app('chat', { home: '/', status: 'live', create: { label: 'New message', path: '/?new=1' } }),
  app('contacts', { home: '/', status: 'live' }),
  app('drive', { home: '/', status: 'live' }),
  app('docs', { home: '/', status: 'live', create: { label: 'New document', path: '/?new=1' } }),
  app('sheets', { home: '/', status: 'live', create: { label: 'New spreadsheet', path: '/?new=1' } }),
  app('slides', { home: '/', status: 'live', create: { label: 'New presentation', path: '/?new=1' } }),
  app('tasks', { home: '/', status: 'live', create: { label: 'New task', path: '/?new=1' } }),
  // Wiki and Notes: two front doors of one product, Pages (D-111).
  app('notes', { home: '/', status: 'live', create: { label: 'New note', path: '/?new=1' } }),
  app('wiki', { home: '/', status: 'live', create: { label: 'New page', path: '/?new=1' } }),
  app('search', { home: '/', status: 'live' }),
  app('ai', { home: '/', status: 'live' }),
  app('account', { home: '/', status: 'live' }),
];

/** The app's origin, derived from Dugzi Account's URL (which every app already has in its config). */
export function appOrigin(app: Pick<DugziApp, 'host'>, accountsUrl: string): string {
  const u = new URL(accountsUrl);
  u.hostname = /^accounts\./.test(u.hostname) ? u.hostname.replace(/^accounts\./, `${app.host}.`) : `${app.host}.${u.hostname}`;
  return u.origin;
}

/**
 * A link that opens `app` as `account` (D-028): through the app's own sign-in with `hint=<account id>`,
 * which is silent when that account is signed in to Dugzi on this device and lands with `?account=`,
 * so the new tab or page uses the same account as the one you came from. Dugzi Account takes `?account=` directly.
 */
export function appHref(app: DugziApp, accountsUrl: string, account?: string, path = app.home): string {
  const origin = appOrigin(app, accountsUrl);
  // Only an account ID in the one public form (`usr_…`, D-135) is passed on; anything else opens plainly.
  if (!isId('usr', account)) return origin + path;
  if (app.id === 'account') return `${origin}${path}${path.includes('?') ? '&' : '?'}account=${encodeURIComponent(account)}`;
  return `${origin}/api/auth/login?hint=${encodeURIComponent(account)}&return_to=${encodeURIComponent(path)}`;
}

